import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminFilterBar from '../../components/admin/AdminFilterBar';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminMetricCard from '../../components/admin/AdminMetricCard';
import AdminDataTable from '../../components/admin/AdminDataTable';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';
import AdminStatusBadge from '../../components/admin/AdminStatusBadge';

export default function AdminCommercialEntitlementsPage() {
  const { t } = useLanguage();
  const [userId, setUserId] = useState('1');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const id = String(userId || '').trim();
    if (!/^\d+$/.test(id)) {
      setErr(true);
      return;
    }
    setLoading(true);
    setErr(false);
    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_COMMERCIAL_EFFECTIVE_PLAN(id)), {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('bad');
      setData(await res.json());
    } catch {
      setErr(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const eff = data?.effective;
  const resolved = eff?.resolvedPlan;

  const quotaRows = useMemo(() => {
    const per = eff?.perResource || {};
    return Object.keys(per).map((k) => {
      const q = per[k] || {};
      return {
        __rowKey: k,
        resource: k,
        allowed: q.allowed != null ? String(q.allowed) : '—',
        used: q.used != null ? String(q.used) : '—',
        limit: q.limit != null ? String(q.limit) : '—',
        remaining: q.remaining != null ? String(q.remaining) : '—',
        engine: q.quota_engine || '—',
      };
    });
  }, [eff]);

  const cols = useMemo(
    () => [
      { key: 'resource', header: t('adminKit.colResource') },
      { key: 'allowed', header: t('adminKit.colAllowedRaw') },
      { key: 'used', header: t('adminKit.colUsed') },
      { key: 'limit', header: t('adminKit.colLimit') },
      { key: 'remaining', header: t('adminKit.colRemaining') },
      { key: 'engine', header: t('adminKit.colEngine') },
    ],
    [t]
  );

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navCommercial'), to: '/admin/commercial/plans' },
      { label: t('adminHub.navEntitlements') },
    ],
    [t]
  );

  return (
    <AdminHubPageFrame
      titleKey="adminHub.entitlementsTitle"
      descKey="adminHub.entitlementsDesc"
      breadcrumbItems={crumbs}
    >
      <section className="admin-section-card">
        <AdminFilterBar>
          <label>
            {t('adminHub.userIdLabel')}
            <input value={userId} onChange={(e) => setUserId(e.target.value)} type="text" inputMode="numeric" />
          </label>
          <button type="button" className="admin-hub-btn" onClick={load} disabled={loading}>
            {loading ? t('adminPage.loading') : t('adminHub.loadAction')}
          </button>
        </AdminFilterBar>
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : null}
        {data?.success && eff ? (
          <div className="admin-hub-kit-stack">
            <AdminSectionHeader title={t('adminKit.entitlementUserTitle')} subtitle={t('adminKit.entitlementUserSub')} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard label={t('adminKit.userId')} value={String(eff.userId || '—')} />
              <AdminMetricCard label={t('adminKit.planId')} value={eff.planId || '—'} />
              <AdminMetricCard label={t('adminKit.accountKind')} value={eff.kind || '—'} />
              <AdminMetricCard
                label={t('adminKit.resolverEnforcement')}
                value={<AdminStatusBadge tone="neutral">{eff.enforcement || '—'}</AdminStatusBadge>}
              />
            </div>
            <AdminSectionHeader title={t('adminKit.resolvedPlanTitle')} level={3} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard label={t('adminKit.planCode')} value={resolved?.planCode || '—'} />
              <AdminMetricCard label={t('adminKit.resolverSource')} value={resolved?.source || '—'} />
              <AdminMetricCard label={t('adminKit.audienceType')} value={resolved?.audience_type || '—'} />
            </div>
            <AdminSectionHeader title={t('adminKit.perResourceQuotasTitle')} level={3} />
            <AdminDataTable columns={cols} rows={quotaRows} emptyMessage={t('adminKit.tableEmpty')} />
            <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={data} />
          </div>
        ) : null}
      </section>
    </AdminHubPageFrame>
  );
}
